import { Token } from "./token";
import { TokenType } from "./token-type";
import { TokenGroup } from "./token-group";

export class Tokeniser {

    public tokenise(query: string): TokenGroup {
        return this.polishTokens(this.getTokens(query));
    }

    /**
     * Do an inital pass over the tokens to clean/polish them. Checks include
     * - Changing PotentialFieldNames to DefiniteFieldNames if followed by [
     * - Changing PotentialDateOperators to words if they are not within a year field
     */
    public polishTokens(tokenGroup: TokenGroup): TokenGroup {
        const tokens = tokenGroup.Tokens;
        for (let i = 0; i < tokens.length; i++) {
            const current = tokens[i];
            const next = tokens[i + 1];
            const prev = i > 0 ? tokens[i - 1] : null ;
            if (current.type === TokenType.PotentialFieldName) {
                if (next && next.type === TokenType.OpenSquareBracket) {
                    current.type = TokenType.DefiniteFieldName;
                } else {
                    current.type = TokenType.Word;
                }
            }
        }
        let withinYear = false;
        tokens.forEach((current) => { // TODO not sure this does anything anymore
            if (current.type === TokenType.DefiniteFieldName) {
                if (current.value === "year") {
                    withinYear = true;
                }
            }
            if (current.type === TokenType.PotentialDateOperator && !withinYear) {
                current.type = TokenType.Word;
            }
            if (current.type === TokenType.CloseSquareBracket) {
                withinYear = false;
            }
        });

        tokenGroup.Tokens = tokens;
        return tokenGroup;
    }

    public tokeniseChunk(chunk: string): Token[] {
        const returnVal: Token[] = [];
        // We need to add spaces before the key symbols for the regex to pick them up.

        const tokenStrings: string[] = [];
        const regexMatches = this.getMatches(chunk);

        regexMatches.forEach((match) => {
            const result = match[0].trim();
            if (result !== "") {
                tokenStrings.push(result);
            }
        });
        tokenStrings.forEach((r) => {
            returnVal.push(new Token(r, this.detectNodeType(r)));
        });
        return returnVal;
    }

    public detectNodeType(input: string): TokenType {

        input = input.toLowerCase();
        input = input.trim();

        const validityChecker = /^[^\s]*$/;
        const regexResult = input.match(validityChecker);
        const isValid = regexResult !== null && regexResult.length > 0;
        if (!isValid) {
            throw new Error("Malformed Token");
        }
        if (input.match(/^[0-9]{1,7}$/)) {
            return TokenType.Number;
        }

        if (input.match(/^(w|W)\/[1-9][0-9]{0,6}$/)) { // Match w/10 or w/99 or w/5 but not w/02. Upper limit on length to prevent server error
            return TokenType.WOperator;
        }

        if (input.match(/^[a-zA-Z]+\*$/)) { // One or more letters followed by *
            return TokenType.Wildcard;
        }

        switch (input) {
            case "and":
            case "&":
                return TokenType.And;
            case "or":
            case "|":
                return TokenType.Or;
            case "(":
                return TokenType.OpenBracket;
            case "[":
                return TokenType.OpenSquareBracket;
            case ")":
                return TokenType.CloseBracket;
            case "]":
                return TokenType.CloseSquareBracket;
            case "!":
            case "not":
                return TokenType.Not;
            case "year":
            case "court":
            case "title":
            case "citation":
            case "judge":
            case "category":
            case "fulltext":
            case "addressedto":
            case "advocategeneral":
            case "applicant":
            case "authoritativelanguage":
            case "countryoforigin":
            case "datecaselodged":
            case "dateofcompliance":
            case "dateofdebate":
            case "dateofdespatch":
            case "dateofdocument":
            case "dateofendofvalidity":
            case "dateofentryintoforce":
            case "dateofnotification":
            case "dateofpublication":
            case "dateofreply":
            case "dateofsignature":
            case "dateofvote":
            case "datesofimplementation":
            case "defendant":
            case "documentauthor":
            case "documentnumber":
            case "documenttype":
            case "descriptor":
            case "amendeditems":
            case "enablingtreaty":
            case "europeanparliamentaryterm":
            case "fulltext":
            case "futureproposals":
            case "judge":
            case "citedcases":
            case "citedlegislation":
            case "citingcases":
            case "legislativebase":
            case "amendingitems":
            case "observers":
            case "ojreference":
            case "parties":
            case "preciselegalpoint":
            case "proceedings":
            case "sector":
            case "subjectmatter":
            case "title":
            case "updatedate":
            case "year":
            case "ecli":
            case "keywords":
            case "date":
            case "headnote":
                return TokenType.PotentialFieldName;
            case "before":
            case "after":
            // case "on":
            case "from":
            case "to":
            case "<":
            case ">":
                return TokenType.PotentialDateOperator;
                // return TokenType.From;
                // return TokenType.To;
            case "near":
                return TokenType.Near;
            case "within":
                return TokenType.Within;
            case "of":
                return TokenType.PotentialOf;
            default:
                return TokenType.Word;
        }

    }

    private getTokens(query: string): TokenGroup {
        const chunks = query.split("\"");
        let areQuotesGood = true;
        if (chunks.length % 2 === 0) {
            areQuotesGood = false;
        }
        let tokens: Token[] = [];
        for (let i = 0; i < chunks.length; i++) {
            if (i % 2 === 0 ) { // Stuff outside quotes
                const res = this.tokeniseChunk(chunks[i]);
                tokens = tokens.concat(res);
            } else { // Stuff inside quotes
                if (chunks[i].trim() !== "") {
                    tokens.push(new Token("\"" + chunks[i] + "\"", TokenType.Phrase));
                }
            }
        }
        const error = areQuotesGood ? "" : "Your search contains an odd number of quotation marks";
        return new TokenGroup(tokens, error);
    }

    /**
     * Apply a Regex to the input string in order to break it down into tokens.
     * This method is somewhat hacky, but is a working solution for now.
     */
    private getMatches(input: string): string[] {
        // If we want to clean up this regex apparently there's a library called XRegExp which supports named capture groups
        input = input.replace(/\)/g, " )");
        input = input.replace(/\(/g, " (");
        input = input.replace(/\]/g, " ]");
        input = input.replace(/\[/g, " [");
        input = input.replace(/!/g, " !");
        // input = input.replace(/&/g, " &");
        input = input.replace(/\|/g, " |");
        input = input + " ";

        // caputure some amount of non-brackets/whitespace/and/or/not characters,
        // which are followed by a bracket or whitespace char,
        // optionally followed by some number of non-brackets/whitespace chars
        const regex = /([^[\]\s()!|]*)[[\]\s()!|][^[\]\s()!|]*?/g;
        const result: string[] = [];
        let temp = null;
        while (temp = regex.exec(input)) {
            result.push(temp);
        }
        return result;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/helpers/search-tree/tokeniser.ts